import React, { useState, useEffect, useContext } from 'react';
import { getErrorMessage } from '../utils/errorHandler';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Calendar, MapPin, Euro, CheckCircle } from 'lucide-react';

const UTC_OFFSET_REGEX = /([zZ]|[+-]\d{2}:\d{2}|[+-]\d{4})$/;

const parseBackendUtcDateTime = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  if (typeof value === 'number') {
    const parsedFromNumber = new Date(value);
    return Number.isNaN(parsedFromNumber.getTime()) ? null : parsedFromNumber;
  }

  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  const normalized = trimmed.replace(' ', 'T');
  const utcCandidate = UTC_OFFSET_REGEX.test(normalized) ? normalized : `${normalized}Z`;
  const parsed = new Date(utcCandidate);
  if (!Number.isNaN(parsed.getTime())) return parsed;

  const fallback = new Date(trimmed);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

const formatUtcDateTime = (value) => {
  const parsed = parseBackendUtcDateTime(value);
  if (!parsed) return 'Data non disponibile';

  return parsed.toLocaleDateString('it-IT', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export default function ActivityPostLoginRegistration() {
  const { token } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate(`/login?redirect=/evento/${token}/registra&register=true`);
      return;
    }
    
    loadActivity();
  }, [token, user]);

  const loadActivity = async () => {
    try {
      const response = await axios.get(`/public/activity/${token}`);
      setActivity(response.data);
    } catch (error) {
      toast.error('Evento non trovato');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    setRegistering(true);
    
    try {
      const response = await axios.post(`/activities/${activity.id}/register`);
      
      if (response.data.needs_payment) {
        const registrationId = response.data.registration_id;
        const checkoutResponse = await axios.post('/payments/create-checkout-session-activity', null, {
          params: {
            registration_id: registrationId
          }
        });

        if (!checkoutResponse.data?.checkout_url) {
          throw new Error('Checkout URL not received');
        }

        toast.info('Reindirizzamento al pagamento Stripe...');
        window.location.href = checkoutResponse.data.checkout_url;
      } else {
        toast.success('Registrazione completata!');
        navigate(`/evento/${token}/conferma`);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nella registrazione'));
    } finally {
      setRegistering(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Caricamento...</p>
        </div>
      </div>
    );
  }

  if (!activity) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12 px-4">
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">{activity.title}</CardTitle>
            <CardDescription className="text-base mt-2">{activity.description}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              {activity.start_datetime && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <span>{formatUtcDateTime(activity.start_datetime)}</span>
                </div>
              )}
              {activity.location && (
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-gray-500" />
                  <span>{activity.location}</span>
                </div>
              )}
              {activity.price > 0 && (
                <div className="flex items-center gap-2">
                  <Euro className="h-4 w-4 text-gray-500" />
                  <span className="font-semibold">€ {activity.price.toFixed(2)}</span>
                </div>
              )}
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-green-800 mb-2">
                <CheckCircle className="h-5 w-5" />
                <span className="font-semibold">Sei loggato come {user?.first_name} {user?.last_name}</span>
              </div>
              <p className="text-sm text-green-700">
                Clicca su "Conferma Registrazione" per completare l&apos;iscrizione all&apos;evento.
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => navigate('/activities')}
                className="flex-1"
              >
                Annulla
              </Button>
              <Button
                onClick={handleRegister}
                disabled={registering || !activity.registration_open}
                className="flex-1"
              >
                {registering
                  ? 'Registrazione...'
                  : activity.price > 0
                    ? 'Conferma Registrazione e Paga'
                    : 'Conferma Registrazione'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
